// utils/pdfExtractor.js
import * as pdfjsLib from 'pdfjs-dist';

/**
 * PDF Extractor pour les documents IFCE
 * 
 * Ce module extrait les données des PDF de l'IFCE (Institut Français du Cheval et de l'Équitation)
 * incluant les informations du cheval, sa généalogie et les liens vers les fiches IFCE.
 * 
 * TESTS DE DÉVELOPPEMENT:
 * Pour lancer les tests, ajoutez ?testpdf dans l'URL de votre application
 * Exemple: http://localhost:3000/?testpdf
 * Les tests ne se lancent PAS automatiquement en production
 */

// ========== CONFIGURATION ==========

// Liste complète des races/stud-books IFCE
const RACES_KEYWORDS = [
  // Races américaines
  'Quarter Horse', 'Paint Horse', 'Appaloosa',
  // Races françaises
  'Selle Francais', 'Selle Français', 'Section A', 'Section B',
  'Origine Constatee', 'Origine Constatée', 'Origine Non Constatée',
  // Races européennes
  'Zangersheide', 'Belgian Warmblood', 'BWP', 'BELGISCH WARBLOEDPAARD VZW',
  'Holsteiner', 'Holsteiner Warmblut', 'Hannoveraner', 'Trakehner', 'Oldenbourg',
  'KWPN', 'Kon. Warm Paard', 'Kon. Warm Paard Nederland', 'Nederland',
  'Warmblut', 'Warm Paard',
  // Races pures
  'Pur-Sang', 'Pur-Sang Anglais', 'Pur-Sang Arabe',
  'Anglo-Arabe', 'Connemara', 'Irish Sport Horse',
  // Autres
  'Lipizzan', 'Akhal-Teke', 'Frison', 'Islandais',
  'Cleveland Bay', 'New Forest', 'Irish Cob',
  'Camargue', 'Lusitanien', 'PRE', 'Fjord',
  'Comtois', 'Percheron', 'Auxois', 'Breton',
  'Cob Normand', 'Trait du Nord', 'Henson',
  'Poney Français de Selle', 'Ardennais'
];

// Configuration du worker PDF.js
let workerConfigured = false;

// Essayer d'abord le worker CDN (plus fiable)
if (!workerConfigured && typeof window !== 'undefined') {
  try {
    // Utiliser le CDN pour le worker (plus stable)
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
    workerConfigured = true;
    console.log('Worker PDF.js configuré via CDN');
  } catch (error) {
    console.log('Erreur configuration CDN:', error);
  }
}

// Si le CDN échoue, essayer l'import dynamique
if (!workerConfigured) {
  try {
    import('pdfjs-dist/build/pdf.worker.min.mjs').then(worker => {
      if (worker && (worker.default || worker)) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = worker.default || worker;
        workerConfigured = true;
        console.log('Worker configuré via import dynamique');
      }
    }).catch(err => {
      console.log('Import dynamique non disponible, utilisation du fallback');
    });
  } catch (error) {
    console.log('Import dynamique non supporté dans cet environnement');
  }
}

// Fallback: worker inline si rien d'autre ne fonctionne
if (!workerConfigured) {
  try {
    const workerScript = `
      self.onmessage = function(e) {
        self.postMessage({data: 'Worker inline actif'});
      };
    `;
    const blob = new Blob([workerScript], { type: 'application/javascript' });
    const workerUrl = URL.createObjectURL(blob);
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
    console.log('Worker inline configuré');
  } catch (error) {
    console.log('Worker inline échoué, mode synchrone');
    pdfjsLib.GlobalWorkerOptions.workerSrc = '';
  }
}

console.log('PDF.js version:', pdfjsLib.version);

// ========== FONCTION PRINCIPALE D'EXTRACTION ==========

/**
 * Extrait les données d'un PDF IFCE avec les liens
 */
export const extractIFCEData = async (file) => {
  try {
    console.log('=== DÉBUT EXTRACTION PDF RÉELLE ===');
    console.log('Fichier:', file.name, 'Taille:', file.size, 'Type:', file.type);
    
    // Extraire le lien IFCE direct depuis le nom du fichier
    let mainHorseLink = null;
    const fileName = file.name;
    // Pattern: nom-du-cheval-HASH-infos-generales.pdf
    const fileNameMatch = fileName.match(/^(.+?)(-infos-generales)(\.pdf)?$/i);
    if (fileNameMatch) {
      const horseSlug = fileNameMatch[1]; // ex: kayenne-dla-colline-z-KPMwJmKjRcG5E24Skfjjzg
      mainHorseLink = `https://infochevaux.ifce.fr/fr/${horseSlug}/infos-generales`;
      console.log('Lien principal extrait du nom de fichier:', mainHorseLink);
    }
    
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      throw new Error('Le fichier n\'est pas un PDF valide');
    }
    
    console.log('Conversion en ArrayBuffer...');
    const arrayBuffer = await file.arrayBuffer();
    console.log('ArrayBuffer créé, taille:', arrayBuffer.byteLength);
    
    console.log('Chargement du PDF...');
    let pdf;
    try {
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        verbosity: 0,
        isEvalSupported: false,
        disableAutoFetch: true,
        disableStream: true
      });
      
      pdf = await loadingTask.promise;
      console.log('PDF chargé avec succès, pages:', pdf.numPages);
    } catch (pdfError) {
      console.error('Erreur chargement PDF:', pdfError);
      
      try {
        console.log('Tentative avec configuration minimale...');
        const simpleTask = pdfjsLib.getDocument({ data: arrayBuffer });
        pdf = await simpleTask.promise;
        console.log('PDF chargé en mode simple, pages:', pdf.numPages);
      } catch (simpleError) {
        console.error('Erreur chargement simple:', simpleError);
        throw new Error('Impossible de lire le fichier PDF');
      }
    }
    
    // Structure pour stocker texte et liens
    let fullText = '';
    let extractedLinks = {};
    
    for (let pageNum = 1; pageNum <= Math.min(pdf.numPages, 5); pageNum++) {
      try {
        console.log(`Extraction page ${pageNum}...`);
        const page = await pdf.getPage(pageNum);
        
        // 1. Extraire le texte avec positions
        const textContent = await page.getTextContent();
        let pageTextParts = [];
        let currentPosition = 0;
        
        textContent.items.forEach(item => {
          const text = item.str || '';
          pageTextParts.push({
            text: text,
            x: item.transform ? item.transform[4] : 0,
            y: item.transform ? item.transform[5] : 0,
            width: item.width || 0,
            height: item.height || 0,
            startPos: currentPosition,
            endPos: currentPosition + text.length
          });
          currentPosition += text.length + 1;
        });
        
        // 2. Extraire les annotations (liens)
        const annotations = await page.getAnnotations();
        
        for (const annotation of annotations) {
          if (annotation.subtype === 'Link' && annotation.url) {
            console.log('Annotation complète:', annotation);
            
            const rect = annotation.rect;
            let horseName = '';
            
            // Collecter le texte dans le rectangle du lien
            let linkTextParts = [];
            let minX = rect[0];
            let maxX = rect[2];
            let minY = rect[1];
            let maxY = rect[3];
            
            for (const textPart of pageTextParts) {
              // Vérifier si le texte est dans le rectangle
              if (textPart.x >= minX - 1 && 
                  textPart.x <= maxX + 1 &&
                  textPart.y >= minY - 1 && 
                  textPart.y <= maxY + 1) {
                
                linkTextParts.push({
                  text: textPart.text,
                  x: textPart.x,
                  width: textPart.width || 0
                });
              }
            }
            
            // Trier et assembler
            linkTextParts.sort((a, b) => a.x - b.x);
            
            // Assembler le texte intelligemment
            let assembledText = '';
            let currentX = minX;
            
            for (const part of linkTextParts) {
              // Si ce morceau dépasse largement le rectangle, on s'arrête
              if (part.x > maxX + 5) break;
              
              assembledText += part.text + ' ';
              currentX = part.x + part.width;
              
              // Si on a déjà un nom complet avec code pays, on peut s'arrêter
              if (assembledText.match(/[A-ZÀ-ÿ][A-ZÀ-ÿ\s\-'().]+\([A-Z]{3}\)/)) {
                break;
              }
            }
            
            horseName = assembledText.trim();
            
            // Nettoyer le nom du lien
            horseName = cleanLinkHorseName(horseName);
            
            // Valider et ajouter
            if (horseName && horseName.length >= 3 && horseName.length <= 60 && /[A-ZÀ-ÿ]/.test(horseName)) {
              extractedLinks[horseName] = annotation.url;
              console.log(`Lien trouvé: "${horseName}" -> ${annotation.url}`);
            }
          }
        }
        
        // 3. Construire le texte complet de la page
        const pageText = pageTextParts.map(part => part.text).join(' ');
        fullText += pageText + ' ';
        
        page.cleanup();
        
      } catch (pageError) {
        console.error(`Erreur page ${pageNum}:`, pageError);
      }
    }
    
    console.log('Texte total:', fullText.length, 'caractères');
    console.log('Liens extraits:', Object.keys(extractedLinks).length);
    console.log('Liens:', extractedLinks);
    
    if (fullText.length === 0) {
      throw new Error('Aucun texte trouvé dans le PDF');
    }
    
    // Parser les données avec contexte des liens
    const extractedData = parseIFCETextWithLinks(fullText, extractedLinks);
    
    // NOUVELLE STRUCTURE : Créer la structure ifceLinks avec liens stables
    extractedData.ifceLinks = {
      main: mainHorseLink || null,
      pere: null,
      mere: null,
      pereDeMere: null,
      all: extractedLinks // Garder tous les liens pour référence
    };
    
    // Associer les liens aux parents
    if (extractedData.pere && extractedLinks[extractedData.pere]) {
      extractedData.ifceLinks.pere = extractedLinks[extractedData.pere];
    }
    
    if (extractedData.mere && extractedLinks[extractedData.mere]) {
      extractedData.ifceLinks.mere = extractedLinks[extractedData.mere];
    }
    
    if (extractedData.pereDeMere && extractedLinks[extractedData.pereDeMere]) {
      extractedData.ifceLinks.pereDeMere = extractedLinks[extractedData.pereDeMere];
    }
    
    // Compatibilité : garder aussi l'ancienne structure (temporairement)
    extractedData.links = extractedLinks;
    
    console.log('Données extraites avec liens stables:', extractedData);
    
    return extractedData;
    
  } catch (error) {
    console.error('=== ERREUR EXTRACTION ===');
    console.error('Message:', error.message);
    throw error;
  }
};

// ========== FONCTIONS DE NETTOYAGE ==========

/**
 * Nettoie le nom d'un cheval extrait d'un lien
 */
const cleanLinkHorseName = (name) => {
  if (!name) return '';
  
  let cleaned = name;
  
  // Enlever les années (4 chiffres) et les espaces multiples
  cleaned = cleaned.replace(/\s+\d{4}\s+/g, ' ').replace(/\s{2,}/g, ' ');
  
  // Garder jusqu'au code pays si présent
  const withCountryMatch = cleaned.match(/^([A-ZÀ-ÿ][A-ZÀ-ÿ\s\-'().]+?\([A-Z]{3}\))/);
  if (withCountryMatch) {
    return withCountryMatch[1].trim();
  }
  
  // Sinon, couper avant les mots-clés de race en utilisant la liste complète
  const racePattern = new RegExp(`\\s+(${RACES_KEYWORDS.map(r => r.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')}).*$`, 'i');
  cleaned = cleaned.replace(racePattern, '');
  
  return cleaned.trim();
};

/**
 * Nettoie le nom d'un cheval (version pour parsing général)
 */
const cleanHorseName = (name) => {
  if (!name) return '';
  
  return name
    .replace(/^\s*\d+\s*/, '') // Supprimer les numéros au début
    .replace(/\s+alias\s+.+$/, '') // Supprimer les alias
    .replace(/\s+\d{4}\s+/g, ' ') // Supprimer les années
    .replace(/\s{2,}/g, ' ') // Normaliser les espaces
    .trim();
};

// ========== PARSING DU TEXTE ==========

/**
 * Parse le texte du PDF avec utilisation intelligente des liens
 */
const parseIFCETextWithLinks = (text, extractedLinks = {}) => {
  console.log('=== PARSING TEXTE AVEC LIENS ===');
  const data = {};
  
  const cleanText = text.replace(/\s+/g, ' ').trim();
  console.log('Texte nettoyé (300 premiers chars):', cleanText.substring(0, 300));
  
  // Créer une map inversée pour retrouver rapidement les URLs par nom
  const nameToUrl = {};
  Object.entries(extractedLinks).forEach(([name, url]) => {
    nameToUrl[name.toLowerCase()] = { name, url };
  });
  
  // Pattern générique pour couper aux races
  const raceStopPattern = `(?:\\s+(?:${RACES_KEYWORDS.map(r => r.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})|\\s*Mère|\\s*Père de mère|\\s*Rechercher|$)`;
  
  // ========== EXTRACTION DU NOM PRINCIPAL ==========
  const nomPatterns = [
    /Info chevaux\s+([A-ZÀ-ÿ][A-ZÀ-ÿ\s\-'()]+?)(?:\s+(?:Zangersheide|Selle|Origine|Belgian|Femelle|Male|Mâle|Hongre)|,)/i,
    /^([A-ZÀ-ÿ][A-ZÀ-ÿ\s\-'()]{3,40})(?:\s+(?:Zangersheide|Selle|Origine|Belgian))/im
  ];
  
  for (const pattern of nomPatterns) {
    const match = cleanText.match(pattern);
    if (match) {
      let nom = cleanHorseName(match[1]);
      const linkMatch = nameToUrl[nom.toLowerCase()];
      if (linkMatch) {
        data.nom = linkMatch.name;
        console.log('NOM trouvé via lien:', data.nom);
      } else {
        data.nom = nom;
        console.log('NOM trouvé via pattern:', data.nom);
      }
      break;
    }
  }
  
  // ========== EXTRACTION DU SIRE ==========
  const sireMatch = cleanText.match(/Numéro SIRE\s*:\s*([0-9A-Z]+)/i);
  if (sireMatch) {
    data.sire = sireMatch[1];
    console.log('SIRE trouvé:', data.sire);
  }
  
  // ========== EXTRACTION DU TRANSPONDEUR/UELN ==========
  const transpondeurPatterns = [
    /Numéro UELN\s*:\s*([0-9A-Z]+)/i,
    /transpondeur électronique\s*:\s*([0-9A-Z]+)/i
  ];
  
  for (const pattern of transpondeurPatterns) {
    const match = cleanText.match(pattern);
    if (match) {
      data.transpondeur = match[1];
      console.log('TRANSPONDEUR trouvé:', data.transpondeur);
      break;
    }
  }
  
  // ========== EXTRACTION DE LA DATE DE NAISSANCE ==========
  const dateMatch = cleanText.match(/Date de naissance\s*:\s*([0-9]{1,2}[\/\-\.][0-9]{1,2}[\/\-\.][0-9]{4})/i);
  if (dateMatch) {
    data.dateNaissance = formatDate(dateMatch[1]);
    console.log('DATE NAISSANCE trouvée:', data.dateNaissance);
  }
  
  // ========== EXTRACTION DU SEXE ==========
  const sexeMatch = cleanText.match(/\b(Mâle|Male|Femelle|Hongre|Étalon|Jument)\b/i);
  if (sexeMatch) {
    data.sexe = normalizeSexe(sexeMatch[1]);
    console.log('SEXE trouvé:', data.sexe);
  }
  
  // ========== EXTRACTION DE LA COULEUR ==========
  const couleurMatch = cleanText.match(/\b(Alezan Brûlé|Alezan Brule|Alezan Crins Lavés|Alezan Crins Laves|Alezan|Bai Fonce|Bai Foncé|Bai Brun|Bai|Noir|Gris Pommelé|Gris Pommele|Gris|Palomino|Pie|Rouan|Isabelle|Café au lait|Cafe au lait|Crème|Creme|Blanc|Aubère|Aubere|Louvet|Souris|Champagne|Appaloosa)\b/i);
  if (couleurMatch) {
    let couleur = couleurMatch[1];
    // Normaliser les variations
    couleur = normalizeCouleur(couleur);
    data.couleur = couleur;
    console.log('COULEUR trouvée:', data.couleur);
  }
  
  // ========== EXTRACTION DE LA RACE ==========
  const racePatterns = [
    /Stud-book de naissance\s*:\s*([A-ZÀ-ÿ\s]+?)(?:\s*Muni|\s*Date|\s*$)/i,
    new RegExp(`\\b(${RACES_KEYWORDS.join('|')})\\b`, 'i')
  ];
  
  for (const pattern of racePatterns) {
    const match = cleanText.match(pattern);
    if (match) {
      let race = match[1].trim();
      // Normaliser les noms de races
      race = normalizeRace(race);
      data.race = race;
      console.log('RACE trouvée:', data.race);
      break;
    }
  }
  
  // ========== EXTRACTION POULINIÈRE ==========
  if (cleanText.match(/Poulinière\s+active/i)) {
    data.pouliniere = true;
    console.log('POULINIÈRE active trouvée');
  }
  
  // ========== EXTRACTION GÉNÉALOGIE AVEC LIENS ==========
  // Fonction helper pour extraire un cheval parent
  const extractParent = (label, patterns) => {
    for (const pattern of patterns) {
      const match = cleanText.match(pattern);
      if (match) {
        let parentName = match[1].trim();
        console.log(`${label} - Match trouvé:`, match[0]);
        console.log(`${label} - Nom extrait:`, parentName);
        
        // Chercher d'abord une correspondance exacte dans les liens
        if (extractedLinks[parentName]) {
          console.log(`${label} trouvé via lien exact:`, parentName);
          return parentName;
        }
        
        // Sinon chercher une correspondance partielle
        let bestMatch = null;
        let bestScore = 0;
        
        Object.entries(extractedLinks).forEach(([linkName, url]) => {
          const cleanLinkName = linkName.toLowerCase();
          const cleanParentName = parentName.toLowerCase();
          
          if (cleanLinkName === cleanParentName) {
            bestMatch = linkName;
            bestScore = 100;
          } else if (cleanLinkName.includes(cleanParentName) || cleanParentName.includes(cleanLinkName)) {
            const score = Math.max(
              cleanLinkName.includes(cleanParentName) ? cleanParentName.length : 0,
              cleanParentName.includes(cleanLinkName) ? cleanLinkName.length : 0
            );
            if (score > bestScore) {
              bestScore = score;
              bestMatch = linkName;
            }
          }
        });
        
        if (bestMatch && bestScore > 5) {
          console.log(`${label} trouvé via lien:`, bestMatch);
          return bestMatch;
        } else {
          parentName = cleanHorseName(parentName);
          console.log(`${label} trouvé via pattern (nettoyé):`, parentName);
          return parentName;
        }
      } else {
        console.log(`${label} - Aucun match trouvé avec le pattern:`, pattern);
      }
    }
    return null;
  };
  
  // PÈRE - Utilisation du pattern générique
  const perePattern = new RegExp(`Père\\s*:\\s*([A-ZÀ-ÿ][A-ZÀ-ÿ\\s\\-'().]+?)${raceStopPattern}`, 'i');
  data.pere = extractParent('PÈRE', [perePattern]);
  
  // MÈRE - Utilisation du pattern générique
  const merePattern = new RegExp(`Mère\\s*:\\s*([A-ZÀ-ÿ][A-ZÀ-ÿ\\s\\-'().]+?)${raceStopPattern}`, 'i');
  data.mere = extractParent('MÈRE', [merePattern]);
  
  // PÈRE DE MÈRE - Utilisation du pattern générique avec alias
  const pereDeMerePattern = new RegExp(`Père de mère\\s*:\\s*([A-ZÀ-ÿ][A-ZÀ-ÿ\\s\\-'().]+?)(?:\\s+alias[^)]+)?${raceStopPattern}`, 'i');
  data.pereDeMere = extractParent('PÈRE DE MÈRE', [pereDeMerePattern]);
  
  // Si pas trouvé via extractParent, essayer un pattern plus simple
  if (!data.pereDeMere) {
    const simplePereMatch = cleanText.match(/Père de mère\s*:\s*([A-ZÀ-ÿ][^:]+?)(?:\s+Rechercher|IDENTITÉ|$)/i);
    if (simplePereMatch) {
      const pereDeMereName = simplePereMatch[1].trim();
      // Nettoyer en gardant juste le nom avec code pays
      const cleanedName = pereDeMereName.replace(/\s+alias.*$/i, '').replace(new RegExp(`\\s+(${RACES_KEYWORDS.join('|')}).*$`, 'i'), '').trim();
      
      // Vérifier si on a un lien pour ce nom
      if (extractedLinks[cleanedName]) {
        data.pereDeMere = cleanedName;
        console.log('PÈRE DE MÈRE trouvé (fallback avec lien):', cleanedName);
      } else {
        data.pereDeMere = cleanedName;
        console.log('PÈRE DE MÈRE trouvé (fallback sans lien):', cleanedName);
      }
    }
  }
  
  // ========== EXTRACTION DU NAISSEUR ==========
  const naisseurPatterns = [
    /Naisseur\s*:\s*[0-9%]*\s*([A-ZÀ-ÿ][A-ZÀ-ÿ\s\-'\.]+(?:E\.A\.R\.L\.|S\.A\.R\.L\.|S\.A\.S\.|S\.C\.E\.A\.|EARL|SARL|SAS|SCEA)?[A-ZÀ-ÿ\s\-'\.]*?)(?:\s+Adresse\s+inconnue|\s+Adresse|\s+\d{1,2}\s+[A-ZÀ-ÿ]|\s+\d{5}|$)/i,
    /Naisseur\s*:\s*[0-9%]*\s*([A-ZÀ-ÿ].+?)(?:\s+Adresse|\s+\d{5}|$)/i
  ];
  
  for (const pattern of naisseurPatterns) {
    const match = cleanText.match(pattern);
    if (match) {
      data.naisseur = match[1].trim();
      console.log('NAISSEUR trouvé:', data.naisseur);
      break;
    }
  }
  
  console.log('Données parsées finales:', data);
  return data;
};

// ========== FONCTIONS DE NORMALISATION ==========

const formatDate = (dateStr) => {
  try {
    const parts = dateStr.split(/[\/\-\.]/);
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];
      
      if (year.length === 4) {
        return `${year}-${month}-${day}`;
      }
    }
  } catch (error) {
    console.error('Erreur formatage date:', error);
  }
  return dateStr;
};

const normalizeSexe = (sexe) => {
  const s = sexe.toLowerCase();
  if (s.includes('mâle') || s.includes('male') || s.includes('étalon')) return 'M';
  if (s.includes('femelle') || s.includes('jument')) return 'F';
  if (s.includes('hongre')) return 'Hongre';
  return sexe;
};

const normalizeCouleur = (couleur) => {
  const c = couleur.toLowerCase();
  if (c === 'bai fonce') return 'Bai Foncé';
  if (c === 'creme') return 'Crème';
  if (c === 'alezan brule') return 'Alezan Brûlé';
  if (c === 'alezan crins laves') return 'Alezan Crins Lavés';
  if (c === 'cafe au lait') return 'Café au lait';
  if (c === 'gris pommele') return 'Gris Pommelé';
  if (c === 'aubere') return 'Aubère';
  // Si pas de normalisation nécessaire, retourner avec première lettre en majuscule
  return couleur.charAt(0).toUpperCase() + couleur.slice(1);
};

const normalizeRace = (race) => {
  // Normaliser les variations connues
  if (race.includes('Selle Franc')) return 'Selle Français';
  if (race.includes('Origine Non Const')) return 'Origine Non Constatée';
  if (race.includes('Origine Constatee')) return 'Origine Constatée';
  if (race === 'STUD BOOK DU SELLE FRANCAIS') return 'Selle Français';
  if (race === 'STUD BOOK ZANGERSHEIDE') return 'Zangersheide';
  if (race === 'BELGISCH WARBLOEDPAARD VZW' || race === 'BWP') return 'Belgian Warmblood';
  if (race === 'Kon. Warm Paard Nederland' || race === 'Kon. Warm Paard') return 'KWPN';
  if (race === 'Holsteiner Warmblut') return 'Holsteiner';
  return race;
};

// ========== VALIDATION ==========

export const validateExtractedData = (data) => {
  const warnings = [];
  const validated = { ...data };
  
  if (Object.keys(data).length === 0) {
    warnings.push('Aucune donnée extraite du PDF');
  }
  
  // Validation spécifique des champs
  if (validated.nom && validated.nom.length > 50) {
    warnings.push(`Nom trop long (${validated.nom.length} caractères), vérifiez l'extraction`);
  }
  
  if (validated.sire && !/^[0-9A-Z]+$/.test(validated.sire)) {
    warnings.push('Format SIRE incorrect (doit contenir uniquement chiffres et lettres majuscules)');
  }
  
  if (validated.dateNaissance && !isValidDate(validated.dateNaissance)) {
    warnings.push('Format de date de naissance incorrect');
  }
  
  // Vérifier la cohérence des liens
  if (validated.ifceLinks) {
    ['pere', 'mere', 'pereDeMere'].forEach(field => {
      if (validated[field] && !validated.ifceLinks[field]) {
        console.log(`Note: ${field} "${validated[field]}" n'a pas de lien IFCE direct dans la structure`);
      }
    });
  }
  
  return { data: validated, warnings };
};

const isValidDate = (dateStr) => {
  const date = new Date(dateStr);
  return date instanceof Date && !isNaN(date);
};

// ========== SECTION TESTS ==========

/**
 * Lance les tests d'extraction
 */
export const runExtractionTests = () => {
  console.log("=== TESTS D'EXTRACTION PDF ===");
  
  // Test nettoyage des noms de liens
  const testLinkNames = [
    { input: "KJENTO V.O.D. (NLD) Kon. Warm Paard", expected: "KJENTO V.O.D. (NLD)" },
    { input: "BLUE SKY GENUINE  2018   Quarter Horse", expected: "BLUE SKY GENUINE" },
    { input: "BELLE STAR DUN IT Origine Constatee", expected: "BELLE STAR DUN IT" },
    { input: "VOLTAIRE (DEU) alias VOLTAIRE 356 Hannoveraner", expected: "VOLTAIRE (DEU)" },
    { input: "CHELENE DE BOISAUBERT (NLD) Kon. Warm Paard Nederland", expected: "CHELENE DE BOISAUBERT (NLD)" },
    { input: "LINGH (NLD) Kon. Warm Paard Nederland", expected: "LINGH (NLD)" },
    { input: "DARK DUN IT Quarter Horse", expected: "DARK DUN IT" },
    { input: "CARNANDO Holsteiner Warmblut", expected: "CARNANDO" },
    { input: "ELIANE EVANS Trakehner", expected: "ELIANE EVANS" },
    { input: "PERKUNOS (DEU) Trakehner", expected: "PERKUNOS (DEU)" }
  ];
  
  console.log("\n--- Test nettoyage noms de liens ---");
  let successCount = 0;
  let failCount = 0;
  
  testLinkNames.forEach((test, index) => {
    const result = cleanLinkHorseName(test.input);
    if (result === test.expected) {
      console.log(`✅ Test ${index + 1}: "${test.input}" → "${result}"`);
      successCount++;
    } else {
      console.error(`❌ Test ${index + 1} ÉCHOUÉ: "${test.input}" → "${result}" (attendu: "${test.expected}")`);
      failCount++;
    }
  });
  
  // Test normalisation des couleurs
  const testColors = [
    { input: "Creme", expected: "Crème" },
    { input: "Bai Fonce", expected: "Bai Foncé" },
    { input: "alezan brule", expected: "Alezan Brûlé" },
    { input: "cafe au lait", expected: "Café au lait" },
    { input: "Gris pommele", expected: "Gris Pommelé" }
  ];
  
  console.log("\n--- Test normalisation couleurs ---");
  testColors.forEach((test, index) => {
    const result = normalizeCouleur(test.input);
    if (result === test.expected) {
      console.log(`✅ Couleur ${index + 1}: "${test.input}" → "${result}"`);
      successCount++;
    } else {
      console.error(`❌ Couleur ${index + 1} ÉCHOUÉ: "${test.input}" → "${result}" (attendu: "${test.expected}")`);
      failCount++;
    }
  });
  
  // Test normalisation des races
  const testRaces = [
    { input: "BELGISCH WARBLOEDPAARD VZW", expected: "Belgian Warmblood" },
    { input: "Kon. Warm Paard Nederland", expected: "KWPN" },
    { input: "Holsteiner Warmblut", expected: "Holsteiner" },
    { input: "Origine Constatee", expected: "Origine Constatée" }
  ];
  
  console.log("\n--- Test normalisation races ---");
  testRaces.forEach((test, index) => {
    const result = normalizeRace(test.input);
    if (result === test.expected) {
      console.log(`✅ Race ${index + 1}: "${test.input}" → "${result}"`);
      successCount++;
    } else {
      console.error(`❌ Race ${index + 1} ÉCHOUÉ: "${test.input}" → "${result}" (attendu: "${test.expected}")`);
      failCount++;
    }
  });
  
  console.log("\n=== RÉSULTAT DES TESTS ===");
  console.log(`✅ Réussis: ${successCount}`);
  console.log(`❌ Échoués: ${failCount}`);
  console.log(`Total: ${successCount + failCount}`);
  
  return failCount === 0;
};

/**
 * GESTION DES TESTS EN DÉVELOPPEMENT
 * 
 * Les tests ne se lancent automatiquement QUE si:
 * 1. On est en mode développement (NODE_ENV === 'development')
 * 2. L'URL contient le paramètre ?testpdf
 * 
 * Pour lancer les tests manuellement:
 * - En développement: ajoutez ?testpdf à votre URL
 * - Via console: appelez directement runExtractionTests()
 */
if (typeof window !== 'undefined' && typeof process !== 'undefined') {
  // Vérifier si on est en développement et si le paramètre testpdf est présent
  const shouldRunTests = 
    process.env.NODE_ENV === 'development' && 
    window.location.search.includes('testpdf');
  
  if (shouldRunTests) {
    // Attendre que le DOM soit chargé
    if (document.readyState === 'loading') {
      window.addEventListener('DOMContentLoaded', () => {
        console.log('🧪 Mode test activé - Lancement des tests PDF Extractor...');
        runExtractionTests();
      });
    } else {
      // DOM déjà chargé
      console.log('🧪 Mode test activé - Lancement des tests PDF Extractor...');
      runExtractionTests();
    }
  }
}